 
 
 
 
 
 
/**
 * Matrix routing service implementation using using the TomTom's
 * [Matrix Routing API](https://developer.tomtom.com/online-routing/online-routing-documentation/matrix-routing).
 *
 * The Online Routing - Matrix service allows calculation of a matrix of route summaries for a set of routes
 * which are defined with origin and destination locations.
 *
 * For every given origin this service calculates
 * the cost of routing from that origin to every given destination. The set of origins and the set of destinations
 * can be thought of as the column and row headers of a table and each cell in the table contains the costs of routing
 * from the origin to the destination for that cell.
 *
 * The following costs are computed for each route:
 * - Travel times
 * - Distances
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "MatrixRouting/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    {{#crossLink "MatrixRouting/go:method"}}{{/crossLink}} method to handle the response.
 *
 * @class MatrixRouting
 * @module Services
 * @uses Chain
 * @uses KeyMixin
 *
 * @constructor
 * @param {Object} [options] Options to be passed to the routing call,
 *     or Array of such options objects to perform batch request.
 * @param {String} [options.key=global API key] An API Key issued
 *     from <a href="http://developer.tomtom.com">Developer Portal</a>
 * @param {Array} [options.origins] A set of origin locations represented by coordinates (lat, lon).
 *     At least one origin is required.
 * @param {Array} [options.destinations] A set of destination locations represented by coordinates (lat, lon).
 *     At least one destination is required.
 * @param {String} [options.routeType] The type of route requested.
 * @param {Boolean} [options.traffic] consider all available traffic information during routing
 * @param {String} [options.avoid] Specifies something that the route calculation should try to avoid when
 *     determining the route. The possible values are documented
 *     {{#crossLink "MatrixRouting/avoid:method"}}here{{/crossLink}}.
 * @param {String} [options.departAt] The date and time of departure from the origin point
 * @param {String} [options.arriveAt] The date and time of arrival at the destination point
 *     response
 * @param {String} [options.travelMode] The mode of travel for the requested route
 * @param {String} [options.hilliness] Degree of hilliness for thrilling route
 * @param {String} [options.windingness] Level of turns for thrilling route
 * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
 *
 * @example
 * ```js
 * var origins = [
 *   { point: { latitude: 51.63685, longitude: 19.4171 } },
 *   { point: { latitude: 51.63685, longitude: 19 } }
 * ];
 * var destinations = [
 *   { point: { latitude: 52.23498, longitude: 21.00849 } },
 *   { point: { latitude: 52.63685, longitude: 18 } }
 * ];
 * function callbackFn(routeGeoJson) {
 *   console.log(routeGeoJson);
 * }
 * var request = tomtom.matrixRouting({
 *   origins: origins,
 *   destinations: destinations,
 *   traffic: true
 * });
 * request.go()
 *   .then(callbackFn);
 * ```
 */
/* jshint nomen:false */
define([
    'rest/routing',
    'model/routing/matrixRouting',
    '../chain',
    '../validators',
    '../converters',
    './routingParameters'
],
function(routing, matrixRoutingModel, chain, validators, converters, getRoutingParameters) {
    'use strict';

    /**
     * A setter and getter for `routeType` attribute.
     *
     * Represent the type of route requested.
     *
     * Notes on specific values:
     *  - `fastest` returns the fastest route.
     *  - `shortest` returns the shortest route by distance.
     *  - `eco` routes balance economy and speed.
     *  - thrilling routes include interesting or challenging roads and use as few motorways as possible. You can
     * choose the level of turns included and also the degree of hilliness. See the hilliness and windingness
     * parameters for how to set this. There is a limit of 900 km on routes planned with routeType=thrilling
     * Default value is fastest
     *
     * @method routeType
     * @chainable
     * @param {String} [routeType] Route type to be used during route calculation.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or route type value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'traffic' attribute.
     * Possible values:
     * true (do consider all available traffic information during routing)
     * false (ignore current traffic data during routing). Note that although the current traffic data is
     * ignored during routing, the effect of historic traffic on effective road speeds is still incorporated.
     * Default is true
     *
     * @method traffic
     * @chainable
     * @param {String|Boolean} [traffic] Boolean flag determine if traffic data should be used to calculate the
     *     route.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Boolean} Method returns the service instance allowing chaining or traffic value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for `avoid` attribute.
     *
     * Specifies something that the route calculation should try to avoid when determining the route.
     * Can be specified multiple times. Possible values:
     *  - `tollRoads` avoids toll roads.
     *  - `motorways` avoids motorways.
     *  - `ferries` avoids ferries.
     *  - `unpavedRoads` avoids unpaved roads.
     *  - `carpools` avoids routes that require use of carpool (HOV/ High Occupancy Vehicle) lanes.
     *  - `alreadyUsedRoads` avoids using the same road multiple times.
     *
     * Most useful in conjunction with `routeType=thrilling`.
     *
     * @method avoid
     * @chainable
     * @param {Array|String} [avoid] Specifies something that the route calculation should try to avoid when
     *     determining the route
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Array} Method returns the service instance allowing chaining or street number value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'departAt' attribute.
     * The date and time of departure from the origin point.
     * Departure times apart from now must be specified as a dateTime.
     * When a time zone offset is not specified, it will be assumed to be that of the origin point.
     * The departAt value must be in the future. The departAt parameter cannot be used in conjunction with
     * arriveAt.
     *
     * @method departAt
     * @chainable
     * @param {String} [departAt] The properly formatted date with time or 'now' value. Date cannot be in the
     *     past.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'arriveAt' attribute.
     * The date and time of arrival at the destination point.
     * It must be specified as a dateTime.
     * When a time zone offset is not specified it will be assumed to be that of the destination point.
     * The arriveAt value must be in the future. The arriveAt parameter cannot be used in conjunction with
     * departAt.
     *
     * @method arriveAt
     * @chainable
     * @param {String} [arriveAt] The properly formatted date with time value. Date cannot be in the past.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'travelMode' attribute.
     * The mode of travel for the requested route. Possible values: _car, truck, taxi, bus, van, motorcycle,
     * bicycle, pedestrian_ Note that the requested travelMode may not be available for the entire route. Where
     * the requested travelMode is not available for a particular section, the <travelMode> element of the
     * response for that section will be other.
     *
     * @method travelMode
     * @chainable
     * @param {String} [travelMode] The primary mean of transportation to be used while routing.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'hilliness' attribute.
     * Degree of hilliness for thrilling route. Possible values: low, normal, high
     * This parameter can only be used in conjunction with routeType thrilling.
     *
     * @method hilliness
     * @chainable
     * @param {String} [hilliness] The level of hilliness on a thrilling route.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'windingness' attribute.
     * Level of turns for thrilling route. Possible values: low, normal, high
     * This parameter can only be used in conjunction with routeType thrilling.
     *
     * @method windingness
     * @chainable
     * @param {String} [windingness] The level of windingness on a thrilling route.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'computeTravelTimeFor' attribute.
     * Specifies whether to return additional travel times using different types of traffic information (none,
     * historic, live) as well as the default best-estimate travel time. Possible values:
     * * none - do not compute additional travel times,
     * * all - compute travel times for all types of traffic information. Specifying all results in the fields
     * noTrafficTravelTimeInSeconds, historicTrafficTravelTimeInSeconds and
     * liveTrafficIncidentsTravelTimeInSeconds being included in the summaries in the route response.
     *
     * @method computeTravelTimeFor
     * @chainable
     * @param {String} [computeTravelTimeFor] Setting _all_ provides additional information regards travel
     *     times.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleEngineType' attribute.
     * Engine type of the vehicle. This parameter is currently only considered for travelMode truck. Valid
     * options are: _combustion, electric_
     *
     * @method vehicleEngineType
     * @chainable
     * @param {String} [vehicleEngineType] The vehicle engine type.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleMaxSpeed' attribute.
     * Maximum speed of the vehicle in km/hour. A value of 0 means that an appropriate value for the vehicle
     * will be determined and applied during route planning. A non-zero value may be overridden during route
     * planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleMaxSpeed
     * @chainable
     * @param {Number} [vehicleMaxSpeed] The maximal allowed vehicle speed in km/h.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleWeight' attribute.
     * Weight of the vehicle in kg. A value of 0 means that weight restrictions are not considered during route
     * planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleWeight
     * @chainable
     * @param {Number} [vehicleWeight] The vehicle weight in kg.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleAxleWeight' attribute.
     * Weight per axle of the vehicle in kg. A value of 0 means that weight restrictions per axle are not
     * considered during route planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleAxleWeight
     * @chainable
     * @param {Number} [vehicleAxleWeight] The vehicle axle wight in kg.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleLength' attribute.
     * Length of the vehicle in meters. A value of 0 means that length restrictions are not considered during
     * route planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleLength
     * @chainable
     * @param {Number} [vehicleLength] The vehicle length in m.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleWidth' attribute.
     * Width of the vehicle in meters. A value of 0 means that width restrictions are not considered during
     * route planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleWidth
     * @chainable
     * @param {Number} [vehicleWidth] The vehicle width in m.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleHeight' attribute.
     * Height of the vehicle in meters. A value of 0 means that height restrictions are not considered
     * during route planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleHeight
     * @chainable
     * @param {Number} [vehicleHeight] The vehicle height in m.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleCommercial' attribute.
     * Vehicle is used for commercial purposes and thus may not be allowed to drive on some roads. This
     * parameter is currently only considered for travelMode truck.
     *
     * @method vehicleCommercial
     * @chainable
     * @param {Boolean} [vehicleCommercial] True if vehicle is used for commercial purposes.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleLoadType' attribute.
     * Types of cargo that may be classified as hazardous materials and restricted from some roads. Available
     * vehicleLoadType values are US Hazmat classes 1 through 9, plus generic classifications for use in other
     * countries.
     *
     * __Use these for routing in US__
     * * USHazmatClass1 Explosives
     * * USHazmatClass2 Compressed gas
     * * USHazmatClass3 Flammable liquids
     * * USHazmatClass4 Flammable solids
     * * USHazmatClass5 Oxidizers
     * * USHazmatClass6 Poisons
     * * USHazmatClass7 Radioactive
     * * USHazmatclass8 Corrosives
     * * USHazmatClass9 Miscellaneous
     *
     * __Use these for routing in all other countries__
     * * otherHazmatExplosive Explosives
     * * otherHazmatGeneral Miscellaneous
     * * otherHazmatHarmfulToWater Harmful to water
     *
     * This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleLoadType
     * @chainable
     * @param {Array|String} [vehicleLoadType] The array of truck cargo classification.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Array} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    var fields = getRoutingParameters(['locations', 'maxAlternatives', 'instructionsType',
        'language', 'computeBestOrder', 'routeRepresentation', 'vehicleHeading', 'report',
        'callback'
    ]);

    var latLonFieldDescription = {
        validator: validators.arrayOf({
            point: {
                validator: validators.objectOf({
                    latitude: {
                        validator: validators.latitude,
                        required: true
                    },
                    longitude: {
                        validator: validators.longitude,
                        required: true
                    }
                })
            }
        }),
        converter: converters.arrayOf({
            point: {
                converter: converters.objectOf({
                    latitude: {
                        converter: converters.latitude,
                        required: true
                    },
                    longitude: {
                        converter: converters.longitude,
                        required: true
                    }
                })
            }
        }),
        required: true
    };

    /**
     * A setter and getter for 'origins' attribute.
     * A set of origin locations represented by points (lat,lon). At least one origin is required.
     *
     * @method origins
     * @chainable
     * @param {Array} [origins] A set of origin locations represented by points (lat,lon).
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */
    fields.origins = latLonFieldDescription;

    /**
     * A setter and getter for 'destinations' attribute.
     * A set of destination locations represented by points (lat,lon). At least one destination is required.
     *
     * @method destinations
     * @chainable
     * @param {Array} [destinations] A set of origin locations represented by points (lat,lon).
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */
    fields.destinations = latLonFieldDescription;

    function handleChain(data, callback) {
        routing.matrix(data, function(response) {
            callback.call(this, matrixRoutingModel(response));
        });
    }

    return function(options) {
        var opt = options || {};
        opt.__serviceType = 'routing';

        return chain(fields, handleChain, opt);
    };
});



// WEBPACK FOOTER //
// ./services/routing/matrixRouting.js